import DataTable from 'react-data-table-component';
import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNotif } from 'context/NotificationContext';
import CustomerDataPlatformService from 'utils/network/customer-data-platform/customer-data-platform.service';
import { Transition } from '@headlessui/react';
import moment from 'moment';

import customerDataPlatformService from 'utils/network/customer-data-platform/customer-data-platform.service';
import { downloadByUrl } from 'utils/DownloadByUrl';
import { STATUS_SUCCESS, PAGINATION, HOURS_TO_SUBTRACT } from 'utils/constants';
import LoadingButton from 'components/LoadingButton';

export default function CustomerDataPlatform() {
  const { notification, setNotification } = useNotif();
  const [showDelete, setShowDelete] = useState(false);
  const [idAction, setIdAction] = useState(null);

  const [isLoading, setIsLoading] = useState(null);

  const [pagination, setPagination] = useState({
    search: PAGINATION.DEFAULT_KEYWORD,
    page: PAGINATION.DEFAULT_PAGE,
    perPage: PAGINATION.DEFAULT_PER_PAGE,
    totalPage: PAGINATION.DEFAULT_TOTAL_PAGE,
    totalData: PAGINATION.DEFAULT_TOTAL_DATA
  });

  const [sortProp, setSortProp] = useState({
    order: 'id',
    sort: 'desc'
  });

  const query = useQueryClient();
  const queryKey = useMemo(() => {
    return `cdp-list--${JSON.stringify({
      page: pagination.page,
      perPage: pagination.perPage,
      search: pagination.search,
      sortProp
    })}`;
  }, [pagination.page, pagination.perPage, pagination.search, sortProp]);

  // get list
  const { data, isLoading: getAudienceGroupListLoading } = useQuery({
    queryKey,
    queryFn: () => {
      return CustomerDataPlatformService.audienceGroupList({
        size: pagination.perPage,
        search: pagination.search,
        page: pagination.page,
        sort_by: sortProp.order,
        sort_dir: sortProp.sort
      });
    },
    onSuccess: (data) => {
      if (data.status !== STATUS_SUCCESS) {
        setNotification({
          ...notification,
          show: true,
          title: 'Error',
          error: true,
          message: data?.data?.message
        });
      }
    }
  });

  const handleSort = useCallback((column, sortDirection) => {
    setSortProp({
      order: column.selector,
      sort: sortDirection
    });
  });

  const handleDelete = (id) => {
    setIdAction(id);
    setShowDelete(true);
  };

  const handleDownload = async (id) => {
    setIsLoading(`download-${id}`);
    const respone = await customerDataPlatformService.downloadAudieceGroup(id);
    const status = respone.status;
    if (status !== STATUS_SUCCESS) {
      const messageRespone = respone.data.message;
      setNotification({
        ...notification,
        show: true,
        title: messageRespone,
        error: true
      });
    } else {
      const data = respone?.data?.data;
      const urlCSV = data?.url;
      downloadByUrl(urlCSV);
    }
    setIsLoading(null);
  };

  const { mutate: handleDeleteAudienceGroup } = useMutation(
    () => {
      setIsLoading(`delete-${idAction}`);
      return CustomerDataPlatformService.deleteById(idAction);
    },
    {
      onSuccess: (data) => {
        const status = data?.status;
        if (status !== STATUS_SUCCESS) {
          const messageRespone = data.data.message;
          setNotification({
            ...notification,
            show: true,
            title: messageRespone,
            error: true
          });
        } else {
          setNotification({
            ...notification,
            show: true,
            title: 'Success Delete Audience Group',
            error: false
          });
          query.invalidateQueries([queryKey]);
        }
        setShowDelete(false);
        setIsLoading(null);
      },
      onError: () => {
        setNotification({
          ...notification,
          show: true,
          title: 'Error Delete Audience Group',
          error: true
        });
        setIsLoading(null);
      }
    }
  );

  const handlePerRowsChange = useCallback(
    (newPerPage, page) => {
      setPagination({
        ...pagination,
        page,
        perPage: newPerPage,
        totalPage: Math.ceil((data?.data?.total_data || 0) / newPerPage)
      });
    },
    [pagination, data]
  );

  function handlePageChange(page) {
    setPagination({
      ...pagination,
      page
    });
  }

  const searchDebounce = useRef();

  const columns = [
    {
      name: 'Created Date',
      sortable: true,
      cell: function render(row) {
        return (
          <>
            {moment(row?.createdAt)
              .subtract(HOURS_TO_SUBTRACT, 'hours')
              .format('DD MMMM YYYY HH:mm')}
          </>
        );
      }
    },
    {
      name: 'Audience Group Name',
      cell: function render(row) {
        return <>{row?.title}</>;
      }
    },
    {
      name: 'Total Audience',
      cell: function render(row) {
        return <>{row?.audienceCount}</>;
      }
    },
    {
      name: 'Actions',
      cell: function render(row) {
        return (
          <div className="w-full flex flex-wrap gap-2 my-2">
            <Link
              to={`${row?.id}`}
              className="flex items-center py-1  px-2 text-center bg-gray-100 border font-semibold  rounded-lg leading-5 transition-colors duration-150  hover:bg-gray-300 focus:outline-none focus:shadow-outline-gray"
            >
              View Detail
            </Link>

            <button
              onClick={() => handleDelete(row?.id)}
              className="flex items-center py-1  px-2 text-center bg-gray-100 border font-semibold  rounded-lg leading-5 transition-colors duration-150  hover:bg-gray-300 focus:outline-none focus:shadow-outline-gray"
            >
              {isLoading === `delete-${row?.id}` && <LoadingButton />}
              Delete
            </button>
            <button
              onClick={() => handleDownload(row?.id)}
              className="flex items-center py-1  px-2 text-center bg-gray-100 border font-semibold  rounded-lg leading-5 transition-colors duration-150  hover:bg-gray-300 focus:outline-none focus:shadow-outline-gray"
            >
              {isLoading === `download-${row?.id}` && <LoadingButton />}
              Download
            </button>
          </div>
        );
      }
    }
  ];
  return (
    <>
      <div>
        <div className="flex flex-row items-center justify-between">
          <h2 className="my-6 text-xl font-semibold text-gray-700 dark:text-gray-200">
            Customer Data Platform
          </h2>
          <div className="flex flex-row">
            <Link
              to="new"
              className=" flex flex-wrap px-4 py-2 mt-3 text-sm gap-4 items-center font-medium text-center text-white border border-transparent rounded-lg leading-5 transition-colors duration-150 bg-secondary hover:bg-yellow-700 focus:outline-none focus:shadow-outline-purple"
            >
              <p> Create</p>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13 19C13 19.34 13.04 19.67 13.09 20H4C3.46957 20 2.96086 19.7893 2.58579 19.4142C2.21071 19.0391 2 18.5304 2 18V6C2 4.89 2.89 4 4 4H10L12 6H20C20.5304 6 21.0391 6.21071 21.4142 6.58579C21.7893 6.96086 22 7.46957 22 8V13.81C21.39 13.46 20.72 13.22 20 13.09V8H4V18H13.09C13.04 18.33 13 18.66 13 19ZM20 18V15H18V18H15V20H18V23H20V20H23V18H20Z"
                  fill="white"
                />
              </svg>
            </Link>
          </div>
        </div>
        <div className="bg-white py-1 px-4">
          <div className="flex flex-row items-center justify-between">
            <h2 className="my-6 text-xl font-semibold text-gray-700 dark:text-gray-200">
              Audience Group List
            </h2>
            <div className="flex flex-row">
              <div className="relative">
                <input
                  className="block w-full px-3 py-2 text-sm border appearance-none rounded-md dark:border-gray-600 dark:bg-gray-700 focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:text-gray-300 dark:focus:shadow-outline-gray form-input"
                  placeholder="audience group name"
                  name="search"
                  onChange={({ target: { value } }) => {
                    if (searchDebounce.current) {
                      clearTimeout(searchDebounce.current);
                    }

                    searchDebounce.current = setTimeout(() => {
                      setPagination({ ...pagination, search: value });
                    }, 800);
                  }}
                />
                <div className="absolute top-0 bottom-0 flex flex-row items-center h-full right-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white mb-4 px-4 border border-gray-200 rounded-md">
            {/* TABLE */}
            <DataTable
              columns={columns}
              data={data?.data?.data?.items || []}
              responsive
              sortServer
              onSort={handleSort}
              progressPending={getAudienceGroupListLoading}
              pagination
              paginationServer
              paginationTotalRows={data?.data?.totalItems}
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={handlePageChange}
            />
          </div>
        </div>
      </div>

      {/* Delete Section */}
      <Transition
        show={showDelete}
        enter="transition-all ease-in duration-100"
        enterFrom="opacity-0"
        enterTo="opacity-100"
      >
        <div className="fixed inset-0 z-20 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0">
              <div className="absolute inset-0 bg-gray-100 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>

            <div
              className="inline-block p-8 overflow-hidden text-left align-bottom bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
            >
              <h3 className="font-bold">Delete Confirmation</h3>
              <div
                onClick={() => setShowDelete(false)}
                className="absolute cursor-pointer top-3 right-3"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p>Are you sure to delete this item?</p>
              <div className="flex flex-row-reverse">
                <button
                  onClick={() => setShowDelete(false)}
                  className="px-4 py-2 mt-3 text-sm font-medium text-center text-white border border-transparent rounded-lg leading-5 transition-colors duration-150 bg-secondary hover:bg-yellow-700 focus:outline-none focus:shadow-outline-purple"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAudienceGroup}
                  className="px-4 py-2 mt-3 text-sm font-medium text-center border border-transparent rounded-lg leading-5 transition-colors duration-150 focus:outline-none focus:shadow-outline-purple"
                >
                  {isLoading === `delete-${idAction}` ? (
                    <LoadingButton />
                  ) : (
                    <p>Yes, Delete</p>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </>
  );
}
